// saas-monetization-plans v2 — WU-L: PlanForm.
//
// Four clearly-separated components per plan (design §2.1/§2.2/§4/§8):
//   1. Subscription   — subscriptionAmountCents / currency / interval
//   2. Metered overage — includedVehicles / overagePriceCents
//   3. Features        — PlanFeatures flags + supportTier
//   4. Commission      — OPTIONAL, advanced/collapsed section, OFF by default
//
// Mirrors MembershipPlanForm's dollars↔cents display-boundary pattern
// (react-hook-form carries DOLLAR values for money fields; setValueAs
// converts to integer cents at submit time).

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import {
  type CreatePlanRequest,
  CreatePlanRequestSchema,
  DEFAULT_CURRENCY,
  UpdatePlanRequestSchema,
} from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { TriangleAlert } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { translateApiError } from '../../../../shared/errors/translateApiError';

// ── Types ────────────────────────────────────────────────────────────────

export interface PlanFormValues {
  name: string;
  description: string;
  subscriptionAmountCents: number; // dollars in the form, converted at submit
  currency: string;
  interval: 'month' | 'year';
  intervalCount: number;
  includedVehicles: number;
  overagePriceCents: number; // dollars in the form, converted at submit
  commissionEnabled: boolean;
  commissionBasisPoints: number;
  features: {
    multiLocation: boolean;
    sms: boolean;
    damageReports: boolean;
    advancedAnalytics: boolean;
    customBranding: boolean;
    whiteLabel: boolean;
    api: boolean;
    supportTier: 'email' | 'priority' | 'dedicated';
  };
}

interface PlanFormProps {
  mode: 'create' | 'edit';
  onSubmit: (values: CreatePlanRequest) => Promise<void>;
  defaultValues?: Partial<PlanFormValues>;
  submitLabel?: string;
  onCancel?: () => void;
}

// ── Dollar ↔ Cent / int helpers (same boundary pattern as MembershipPlanForm) ──

function dollarsToCents(v: string | number | null | undefined): number {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : Math.round(n * 100);
}

function toInt(v: string | number | null | undefined): number {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isNaN(n) ? 0 : Math.round(n);
}

const DEFAULT_FEATURES: PlanFormValues['features'] = {
  multiLocation: false,
  sms: false,
  damageReports: false,
  advancedAnalytics: false,
  customBranding: false,
  whiteLabel: false,
  api: false,
  supportTier: 'email',
};

// ── Component ────────────────────────────────────────────────────────────

export function PlanForm({ mode, onSubmit, defaultValues, submitLabel, onCancel }: PlanFormProps) {
  const { t } = useTranslation('plans');
  const schema = mode === 'create' ? CreatePlanRequestSchema : UpdatePlanRequestSchema;
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PlanFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      subscriptionAmountCents: 0,
      currency: DEFAULT_CURRENCY,
      interval: 'month',
      intervalCount: 1,
      includedVehicles: 0,
      overagePriceCents: 0,
      commissionEnabled: false,
      commissionBasisPoints: 0,
      features: DEFAULT_FEATURES,
      ...defaultValues,
    },
  });

  const commissionEnabled = watch('commissionEnabled');

  async function handleFormSubmit(values: PlanFormValues) {
    setServerError(null);
    try {
      await onSubmit(values as unknown as CreatePlanRequest);
    } catch (e) {
      setServerError(translateApiError(e, t, { fallback: t('form.genericError') }));
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
      {serverError && <FormBanner message={serverError} />}

      {/* ── Basic info ────────────────────────────────────────────────── */}
      <div className="grid gap-6 md:grid-cols-2">
        <Field label={t('form.planName.label')} htmlFor="name" error={errors.name?.message}>
          <Input
            id="name"
            placeholder={t('form.planName.placeholder')}
            disabled={isSubmitting}
            {...register('name')}
          />
        </Field>
        <Field
          label={t('form.description.label')}
          htmlFor="description"
          error={errors.description?.message}
        >
          <Input
            id="description"
            placeholder={t('form.description.placeholder')}
            disabled={isSubmitting}
            {...register('description')}
          />
        </Field>
      </div>

      {/* ── 1. Subscription ──────────────────────────────────────────── */}
      <Section
        title={t('form.sections.subscription.title')}
        description={t('form.sections.subscription.description')}
      >
        <div className="grid gap-6 md:grid-cols-2">
          <Field
            label={t('form.monthlyPrice')}
            htmlFor="subscriptionAmountCents"
            error={errors.subscriptionAmountCents?.message}
          >
            <Input
              id="subscriptionAmountCents"
              type="number"
              min={0}
              step="0.01"
              placeholder="99.99"
              disabled={isSubmitting}
              {...register('subscriptionAmountCents', { setValueAs: dollarsToCents })}
            />
          </Field>
          <Field label={t('form.currency')} htmlFor="currency" error={errors.currency?.message}>
            <Input
              id="currency"
              maxLength={3}
              disabled={isSubmitting}
              {...register('currency', { setValueAs: (v: string) => v.toUpperCase() })}
            />
          </Field>
          <Field
            label={t('form.billingInterval')}
            htmlFor="interval"
            error={errors.interval?.message}
          >
            <select
              id="interval"
              disabled={isSubmitting}
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              {...register('interval')}
            >
              <option value="month">{t('form.intervalOptions.month')}</option>
              <option value="year">{t('form.intervalOptions.year')}</option>
            </select>
          </Field>
          <Field
            label={t('form.intervalCount')}
            htmlFor="intervalCount"
            error={errors.intervalCount?.message}
          >
            <Input
              id="intervalCount"
              type="number"
              min={1}
              disabled={isSubmitting}
              {...register('intervalCount', { setValueAs: toInt })}
            />
          </Field>
        </div>
      </Section>

      {/* ── 2. Metered overage ───────────────────────────────────────── */}
      <Section
        title={t('form.sections.meteredOverage.title')}
        description={t('form.sections.meteredOverage.description')}
      >
        <div className="grid gap-6 md:grid-cols-2">
          <Field
            label={t('form.includedVehicles.label')}
            htmlFor="includedVehicles"
            error={errors.includedVehicles?.message}
          >
            <Input
              id="includedVehicles"
              type="number"
              min={0}
              placeholder={t('form.includedVehicles.placeholder')}
              disabled={isSubmitting}
              {...register('includedVehicles', { setValueAs: toInt })}
            />
          </Field>
          <Field
            label={t('form.overagePricePerVehicle.label')}
            htmlFor="overagePriceCents"
            error={errors.overagePriceCents?.message}
          >
            <Input
              id="overagePriceCents"
              type="number"
              min={0}
              step="0.01"
              placeholder={t('form.overagePricePerVehicle.placeholder')}
              disabled={isSubmitting}
              {...register('overagePriceCents', { setValueAs: dollarsToCents })}
            />
          </Field>
        </div>
      </Section>

      {/* ── 3. Features ───────────────────────────────────────────────── */}
      <Section
        title={t('form.sections.features.title')}
        description={t('form.sections.features.description')}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <FeatureCheckbox
            id="feature-multiLocation"
            label={t('form.features.multiLocation')}
            disabled={isSubmitting}
            {...register('features.multiLocation')}
          />
          <FeatureCheckbox
            id="feature-sms"
            label={t('form.features.sms')}
            disabled={isSubmitting}
            {...register('features.sms')}
          />
          <FeatureCheckbox
            id="feature-damageReports"
            label={t('form.features.damageReports')}
            disabled={isSubmitting}
            {...register('features.damageReports')}
          />
          <FeatureCheckbox
            id="feature-advancedAnalytics"
            label={t('form.features.advancedAnalytics')}
            disabled={isSubmitting}
            {...register('features.advancedAnalytics')}
          />
          <FeatureCheckbox
            id="feature-customBranding"
            label={t('form.features.customBranding')}
            disabled={isSubmitting}
            {...register('features.customBranding')}
          />
          <FeatureCheckbox
            id="feature-whiteLabel"
            label={t('form.features.whiteLabel')}
            disabled={isSubmitting}
            {...register('features.whiteLabel')}
          />
          <FeatureCheckbox
            id="feature-api"
            label={t('form.features.api')}
            disabled={isSubmitting}
            {...register('features.api')}
          />
        </div>

        <Field
          label={t('form.supportTier.label')}
          htmlFor="features.supportTier"
          error={errors.features?.supportTier?.message}
          className="mt-4 max-w-xs"
        >
          <select
            id="features.supportTier"
            disabled={isSubmitting}
            className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-[13px] shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            {...register('features.supportTier')}
          >
            <option value="email">{t('form.supportTier.options.email')}</option>
            <option value="priority">{t('form.supportTier.options.priority')}</option>
            <option value="dedicated">{t('form.supportTier.options.dedicated')}</option>
          </select>
        </Field>
      </Section>

      {/* ── 4. Commission (optional, advanced, OFF by default) ──────────── */}
      <Section
        title={t('form.sections.commission.title')}
        description={t('form.sections.commission.description')}
      >
        <FeatureCheckbox
          id="commissionEnabled"
          label={t('form.commissionEnable')}
          disabled={isSubmitting}
          {...register('commissionEnabled')}
        />

        {commissionEnabled && (
          <div className="mt-4 max-w-xs">
            <Field
              label={t('form.commissionBasisPoints.label')}
              htmlFor="commissionBasisPoints"
              error={errors.commissionBasisPoints?.message}
            >
              <Input
                id="commissionBasisPoints"
                type="number"
                min={0}
                max={10000}
                placeholder={t('form.commissionBasisPoints.placeholder')}
                disabled={isSubmitting}
                {...register('commissionBasisPoints', { setValueAs: toInt })}
              />
            </Field>
          </div>
        )}
      </Section>

      <div className="flex items-center justify-end gap-3 border-t border-jq-sand/70 pt-6">
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            {t('common:actions.cancel')}
          </Button>
        )}
        <Button type="submit" size="sm" disabled={isSubmitting}>
          {isSubmitting ? t('form.saving') : (submitLabel ?? t('form.savePlan'))}
        </Button>
      </div>
    </form>
  );
}

// ── Building blocks ──────────────────────────────────────────────────────

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4 border-t border-jq-sand/70 pt-6 first:border-t-0 first:pt-0">
      <div>
        <p className="text-[14px] font-medium text-jq-charcoal">{title}</p>
        <p className="mt-0.5 text-[12px] text-jq-graphite">{description}</p>
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  error,
  className,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className ?? ''}>
      <Label htmlFor={htmlFor} className="mb-1.5 block text-[13px] font-medium text-jq-charcoal">
        {label}
      </Label>
      {children}
      {error && (
        <p role="alert" className="mt-1.5 text-[12px] text-jq-danger">
          {error}
        </p>
      )}
    </div>
  );
}

interface FeatureCheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
}

const FeatureCheckbox = forwardRef<HTMLInputElement, FeatureCheckboxProps>(function FeatureCheckbox(
  { id, label, ...rest },
  ref,
) {
  return (
    <div className="flex items-center gap-2">
      <input
        ref={ref}
        type="checkbox"
        id={id}
        className="h-4 w-4 rounded accent-jq-primary disabled:cursor-not-allowed"
        {...rest}
      />
      <label htmlFor={id} className="text-[13px] text-jq-graphite">
        {label}
      </label>
    </div>
  );
});

function FormBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
    </div>
  );
}
